'use client';

import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import NextLink from 'next/link';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import type { DrawStage, StageStandings } from '@/draw/types';
import { DrawPlayerLink } from '@/app/[locale]/event/draw/components/DrawPlayerLink';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';
import { poolCuts, poolRows, type PoolCut } from '../libs/poolView';
import { poolGrid, poolProgress, poolUpcoming, rankEdge, type PoolCell, type PoolStatus } from '../libs/poolGrid';
import { useLadderText } from '../libs/ladderText';
import { PoolUpcoming } from './PoolUpcoming';

const formatTime = (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(iso));

const PILL: Record<PoolStatus, string> = {
  notStarted: 'border-border text-muted',
  inProgress: 'border-danger text-danger-emphasis',
  complete: 'border-success text-success-emphasis',
};

const HATCH =
  'bg-surface bg-[repeating-linear-gradient(135deg,transparent,transparent_4px,var(--border)_4px,var(--border)_5px)]';
const WIN_MARK = "after:bg-bracket-path after:absolute after:bottom-1.5 after:left-1/2 after:-ml-[7px] after:h-0.5 after:w-3.5 after:rounded-sm after:content-['']";
const EDGE = {
  advance: "before:bg-bracket-path before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']",
  later: "before:bg-border-strong before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']",
};
const HIDE_WIDE = '@min-[640px]:hidden';
const HIDE_NARROW = '@max-[639px]:hidden';
const STICKY_RANK = '@max-[639px]:sticky @max-[639px]:left-0 @max-[639px]:z-[2] @max-[639px]:bg-card';
const STICKY_NAME = '@max-[639px]:sticky @max-[639px]:left-[34px] @max-[639px]:z-[2] @max-[639px]:bg-card @max-[639px]:w-[118px] @max-[639px]:max-w-[118px] print:min-w-32';
const STICKY_PTS = '@max-[639px]:sticky @max-[639px]:left-[152px] @max-[639px]:z-[2] @max-[639px]:bg-card @max-[639px]:border-r @max-[639px]:border-border';
// Header cells carry the surface tone of the rest of the header; the STICKY_* above are the body's card tone.
const STICKY_HEAD_RANK = '@max-[639px]:sticky @max-[639px]:left-0 @max-[639px]:z-[2] @max-[639px]:bg-surface';
const STICKY_HEAD_NAME = '@max-[639px]:sticky @max-[639px]:left-[34px] @max-[639px]:z-[2] @max-[639px]:bg-surface @max-[639px]:w-[118px] @max-[639px]:max-w-[118px] print:min-w-32';
const STICKY_HEAD_PTS = '@max-[639px]:sticky @max-[639px]:left-[152px] @max-[639px]:z-[2] @max-[639px]:bg-surface @max-[639px]:border-r @max-[639px]:border-border';
const TH = 'bg-surface border-border text-muted border-b px-1.5 py-2 text-center text-[11px] font-semibold whitespace-nowrap';
const WIDE_ROW = '@max-[639px]:sticky @max-[639px]:left-0 @max-[639px]:w-[100cqw]';

interface PoolViewProps {
  stage: DrawStage;
  stages: DrawStage[];
  standings?: StageStandings;
  playerHref?: (id: string) => string | undefined;
  title: string;
  headingId: string;
}

export function PoolView({ stage, stages, standings, playerHref, title, headingId }: PoolViewProps) {
  const t = useTranslations('Event.drawView.pool');
  const tMatches = useTranslations('Matches');
  const text = useLadderText();
  const locale = useLocale();
  const [open, setOpen] = useState<string | null>(null);

  const { rows, started } = poolRows(standings?.standings ?? []);
  const cuts = poolCuts(stage, stages);
  const cells = poolGrid(stage, rows.map((r) => r.standing.playerId));
  const progress = poolProgress(stage);
  const upcoming = progress.status === 'complete' ? [] : poolUpcoming(stage);
  const names = new Map(rows.map((r) => [r.standing.playerId, r.standing.playerName]));
  const isTeam = stage.rounds.some((r) => r.matches.some((m) => m.player1?.displayName || m.player2?.displayName));
  // # name opponents P W L +/- Pts: ten visible columns for three players. The Pts copy beside the name
  // (phones) and the trailing Pts (wider) are never both displayed, and a display:none cell makes no column.
  // A colSpan past the real count adds a phantom column to the fixed layout that takes a share of the width.
  const columns = 2 + rows.length + 5;
  const statusLabel = { notStarted: t('statusNotStarted'), inProgress: t('statusInProgress'), complete: t('statusComplete') }[progress.status];

  const cutLabel = (cut: PoolCut) => {
    const name = text.stageName(cut.stageName);
    if (cut.from === cut.to) return t('advanceOne', { rank: cut.from, stage: name });
    if (cut.from === 1) return t('advanceTop', { count: cut.to, stage: name });
    return t('advanceRange', { from: cut.from, to: cut.to, stage: name });
  };

  const stat = (short: string, long: string, className?: string) => (
    <th scope="col" className={cn(TH, 'w-11', className)}>
      <abbr title={long} className="no-underline">{short}</abbr>
    </th>
  );
  const ptsHead = (className: string) => (
    <th scope="col" className={cn(TH, 'w-12', className)}>
      <abbr title={t('pointsLong')} className="no-underline">{t('pointsShort')}</abbr>
    </th>
  );

  /** The caption line above a cell's time or score: the short match ID, plus RET/LIVE where they apply. */
  const caption = (text: string | null, className?: string) =>
    text ? <span data-match-code className={cn('block font-mono text-[8.5px] leading-[9px] font-semibold tracking-wider', className)}>{text}</span> : null;

  const cellBody = (cell: PoolCell): { content: ReactNode; className?: string } => {
    const code = cell.kind === 'self' ? null : (cell.code ?? null);
    switch (cell.kind) {
      case 'self':
        return { content: <span className="sr-only">–</span>, className: HATCH };
      case 'result': {
        const line = `${cell.games[0]}–${cell.games[1]}`;
        const won = cell.result === 'won';
        const face = (
          <>
            {caption(code, 'text-muted')}
            {cell.retired && <span className="text-muted block text-[8.5px] leading-[9px] tracking-widest">{tMatches('retiredShort')}</span>}
            <Numeric>{line}</Numeric>
          </>
        );
        const name = `${code ? `${code} · ` : ''}${line} (${cell.gameLine})${cell.retired ? ` ${tMatches('retiredShort')}` : ''}`;
        return {
          className: won ? cn('text-foreground font-bold', WIN_MARK) : 'text-muted',
          content:
            cell.scorecardMatchId && cell.gameLine ? (
              <NextLink
                href={`/match/scorecard/${cell.scorecardMatchId}`}
                target="_blank"
                rel="noopener"
                prefetch={false}
                title={cell.gameLine}
                aria-label={name}
                className={cn('absolute inset-0 flex flex-col items-center justify-center text-inherit hover:text-inherit', won ? 'font-bold' : 'font-normal')}
              >
                {face}
              </NextLink>
            ) : (
              face
            ),
        };
      }
      case 'walkover':
        return {
          className: cell.result === 'won' ? cn('text-foreground font-bold', WIN_MARK) : 'text-muted',
          content: (
            <>
              {caption(code, 'text-muted')}
              {t('walkoverShort')}
            </>
          ),
        };
      case 'live': {
        const line = `${cell.games[0]}–${cell.games[1]}`;
        const face = (
          <>
            {code ? (
              <span data-match-code className="block font-mono text-[8.5px] leading-[9px] font-semibold tracking-wider">
                <span aria-hidden>● </span>
                <span className="sr-only">{t('liveCap')} </span>
                {code}
              </span>
            ) : (
              <span className="block text-[8.5px] leading-[9px] tracking-widest">{t('liveCap')}</span>
            )}
            <Numeric>{line}</Numeric>
          </>
        );
        return {
          className: 'text-danger-emphasis text-xs font-bold',
          content: cell.scorecardMatchId ? (
            <NextLink
              href={`/match/scorecard/${cell.scorecardMatchId}`}
              target="_blank"
              rel="noopener"
              prefetch={false}
              title={t('liveTitle')}
              aria-label={`${code ? `${code} · ` : ''}${t('liveAria', { score: line })}`}
              className="absolute inset-0 flex flex-col items-center justify-center font-bold text-inherit hover:text-inherit"
            >
              {face}
            </NextLink>
          ) : (
            face
          ),
        };
      }
      case 'scheduled':
        return {
          className: 'text-muted text-[11px]',
          content: (
            <>
              {caption(code)}
              <LocalDateTime iso={cell.iso} locale={locale} format={formatTime} />
            </>
          ),
        };
      default:
        return {
          content: (
            <>
              {caption(code)}
              –
            </>
          ),
          className: 'text-muted',
        };
    }
  };

  const fullRow = (key: string, className: string, children: ReactNode, rowClass?: string) => (
    <tr key={key} className={rowClass}>
      <td colSpan={columns} className="p-0">
        <div className={cn(WIDE_ROW, className)}>{children}</div>
      </td>
    </tr>
  );

  return (
    <div className="@container space-y-0">
      <header className="flex items-baseline gap-x-2.5 gap-y-1 pb-2.5 @max-[639px]:flex-wrap">
        <h3 id={headingId} tabIndex={-1} className="min-w-0 text-[15px] font-bold tracking-tight focus:outline-none @max-[639px]:flex-1">
          {title}
        </h3>
        <p className="text-muted flex flex-wrap gap-x-1.5 text-xs @max-[639px]:order-last @max-[639px]:basis-full">
          <span>{t(isTeam ? 'teams' : 'players', { count: rows.length })}</span>
          <span aria-hidden>·</span>
          <span>{t('playedOf', { played: progress.played, total: progress.total })}</span>
          {progress.bestOf !== null && (
            <>
              <span aria-hidden>·</span>
              <span>{t('bestOf', { count: progress.bestOf })}</span>
            </>
          )}
        </p>
        <span className={cn('bg-card ml-auto inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', PILL[progress.status])}>
          {progress.status === 'inProgress' && <span aria-hidden className="bg-danger mr-1.5 size-1.5 rounded-full" />}
          {statusLabel}
        </span>
      </header>

      {!started && <p className="bg-surface text-muted mb-2 rounded-md px-3 py-2 text-xs">{t('notStarted')}</p>}

      {rows.length > 0 && (
        <div className="@container border-border overflow-x-auto rounded-[10px] border print:overflow-visible!">
          <table
            style={{ '--pool-min': `${34 + 176 + 44 * 4 + 48 + 50 * rows.length}px` } as CSSProperties}
            className={cn('w-full table-fixed print:table-auto border-separate border-spacing-0 text-[13.5px] @min-[640px]:min-w-(--pool-min)', rows.length >= 6 && '@max-[639px]:min-w-[620px]', 'print:min-w-0!')}
          >
            <caption className="sr-only">{t('resultCaption', { title })}</caption>
            <thead>
              <tr>
                <th scope="col" className={cn(TH, 'w-[34px]', STICKY_HEAD_RANK)}>{t('rank')}</th>
                <th scope="col" className={cn(TH, 'pl-3 text-left @min-[640px]:w-[clamp(11rem,22%,16rem)]', STICKY_HEAD_NAME)}>{isTeam ? t('entrantTeam') : t('entrantPlayer')}</th>
                {ptsHead(cn(HIDE_WIDE, STICKY_HEAD_PTS))}
                {rows.map((r, i) => (
                  <th key={r.standing.playerId} scope="col" data-opponent title={r.standing.playerName} className={cn(TH, 'text-foreground w-[50px] @min-[640px]:w-auto', i === 0 && 'border-border border-l @max-[639px]:border-l-0')}>
                    <span aria-hidden className="border-border-strong bg-card inline-block size-[18px] rounded-full border text-[10.5px] leading-4">
                      <Numeric>{i + 1}</Numeric>
                    </span>
                    <span className="sr-only">{r.standing.playerName}</span>
                  </th>
                ))}
                {stat(t('playedShort'), t('playedLong'), 'border-border border-l')}
                {stat(t('winsShort'), t('winsLong'))}
                {stat(t('lossesShort'), t('lossesLong'))}
                {stat(t('diffShort'), t('diffLong'))}
                {ptsHead(HIDE_NARROW)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const s = row.standing;
                const rank = i + 1;
                const edge = rankEdge(rank, cuts);
                const rowCuts = cuts.filter((c) => c.to === rank);
                const expanded = open === s.playerId;
                const td = 'border-border relative h-[38px] border-b px-1.5 text-center';
                const diff = row.gameDiff > 0 ? `+${row.gameDiff}` : row.gameDiff < 0 ? `−${Math.abs(row.gameDiff)}` : '0';
                const pts = (className: string) => (
                  <td data-pts className={cn(td, 'text-[15px] font-bold', className)}><Numeric>{s.points}</Numeric></td>
                );
                return (
                  <Fragment key={s.playerId}>
                    <tr data-entrant>
                      <td data-edge={edge ?? undefined} className={cn(td, 'text-muted text-[12.5px] font-semibold', edge && EDGE[edge], edge === 'advance' && started && 'text-foreground', STICKY_RANK)}>
                        <Numeric>{started ? s.position : rank}</Numeric>
                      </td>
                      <th scope="row" className={cn(td, 'min-w-0 pl-3 text-left font-medium', STICKY_NAME)}>
                        <div className="flex min-w-0 items-center gap-1">
                          <span className={cn("min-w-0 [&_a]:after:absolute [&_a]:after:inset-0 [&_a]:after:content-['']", NAME_WRAP)}>
                            <DrawPlayerLink playerId={s.playerId} playerHref={playerHref}><NameText name={s.playerName} /></DrawPlayerLink>
                          </span>
                          {started && (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              aria-label={t('showTiebreaks', { name: s.playerName })}
                              onClick={() => setOpen(expanded ? null : s.playerId)}
                              className="text-muted relative z-10 shrink-0 before:absolute before:-inset-4 before:content-['']"
                            >
                              <ChevronDownIcon aria-hidden className={cn('size-3.5 transition-transform', expanded && 'rotate-180')} />
                            </button>
                          )}
                        </div>
                      </th>
                      {pts(cn(HIDE_WIDE, STICKY_PTS))}
                      {cells[i].map((cell, j) => {
                        const body = cellBody(cell);
                        return (
                          <td key={rows[j].standing.playerId} className={cn(td, j === 0 && 'border-border border-l @max-[639px]:border-l-0', body.className)}>
                            {body.content}
                          </td>
                        );
                      })}
                      <td className={cn(td, 'border-border border-l')}><Numeric>{row.played}</Numeric></td>
                      <td className={td}><Numeric>{s.wins}</Numeric></td>
                      <td className={td}><Numeric>{s.losses}</Numeric></td>
                      <td className={cn(td, row.gameDiff > 0 ? 'text-success-emphasis font-semibold' : 'text-muted')}><Numeric>{diff}</Numeric></td>
                      {pts(HIDE_NARROW)}
                    </tr>
                    {expanded &&
                      fullRow(`${s.playerId}-tb`, 'bg-surface text-muted px-3 py-1.5 text-xs', t('tiebreaks', { won: s.gamesWon, lost: s.gamesLost, pct: row.gamePct ?? 0, for: s.gamePointsFor, against: s.gamePointsAgainst }))}
                    {rowCuts.map((cut) => {
                      const first = cuts.indexOf(cut) === 0;
                      return fullRow(
                        `${cut.stageName}-${cut.from}`,
                        'flex h-[22px] items-center gap-2.5 pr-3',
                        <>
                          <span aria-hidden className={cn('flex-1 border-t-[1.5px] border-dashed', first ? 'border-bracket-path' : 'border-border-strong')} />
                          <span className={cn('text-[10.5px] font-semibold whitespace-nowrap', first ? 'text-warning-emphasis' : 'text-muted')}>{cutLabel(cut)}</span>
                        </>,
                        'bg-card',
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
      <p className="text-muted flex flex-wrap items-center gap-x-4 gap-y-1 px-0.5 pt-2 text-[11px]">
        <span><i aria-hidden className="bg-bracket-path mr-1.5 inline-block h-0.5 w-3.5 rounded-sm align-[3px]" />{t('legendWon')}</span>
        <span>{t('legendLost')}</span>
        <span>{t('legendTime')}</span>
        <span className={cn('ml-auto', HIDE_WIDE)}>{t('swipeHint')}</span>
      </p>
      )}

      <PoolUpcoming matches={upcoming} total={progress.played + upcoming.length} names={names} playerHref={playerHref} />
    </div>
  );
}
